import { dbManager } from '../database/index.js';
export class PdfDocumentStorageProvider {
    get pdfDocuments() {
        return dbManager.pdfDocuments;
    }
    get pdfPageInfos() {
        return dbManager.pdfPageInfos;
    }
    get pdfParagraphs() {
        return dbManager.pdfParagraphs;
    }
    constructor() {
        this.initialized = false;
        this.initPromise = null;
    }
    static getInstance() {
        if (!PdfDocumentStorageProvider.instance) {
            PdfDocumentStorageProvider.instance = new PdfDocumentStorageProvider();
        }
        return PdfDocumentStorageProvider.instance;
    }
    async initialize() {
        if (this.initialized)
            return;
        if (this.initPromise)
            return this.initPromise;
        this.initPromise = (async () => {
            // The concept of initializing the provider itself is now a no-op,
            // as dbManager handles the connection dynamically.
            // We keep this method to satisfy the interface and any upstream callers,
            // but it doesn't need to perform any logic.
            this.initialized = true;
        })();
        return this.initPromise;
    }
    async ensureInitialized() {
        if (!this.initialized) {
            await this.initialize();
        }
    }
    async addDocument(document) {
        await this.ensureInitialized();
        const numericId = await this.pdfDocuments.add(document);
        return { id: numericId, type: 'pdf' };
    }
    async addDocumentWithParagraphs(documentData, paragraphs) {
        await this.ensureInitialized();
        const newDocId = await dbManager.transaction('rw', ['pdfDocuments', 'pdfParagraphs'], async () => {
            const numericId = await this.pdfDocuments.add(documentData);
            const paragraphsToStore = paragraphs.map(p => ({
                ...p,
                documentId: numericId,
            }));
            await this.pdfParagraphs.bulkAdd(paragraphsToStore);
            return numericId;
        });
        return { id: newDocId, type: 'pdf' };
    }
    async addDocumentWithAllData(documentData, paragraphs, pageInfos) {
        await this.ensureInitialized();
        const newDocId = await dbManager.transaction('rw', ['pdfDocuments', 'pdfParagraphs', 'pdfPageInfos'], async () => {
            const numericId = await this.pdfDocuments.add(documentData);
            const paragraphsToStore = paragraphs.map(p => ({
                ...p,
                documentId: numericId,
            }));
            await this.pdfParagraphs.bulkAdd(paragraphsToStore);
            const pageInfosToStore = pageInfos.map(p => ({
                ...p,
                documentId: numericId,
            }));
            await this.pdfPageInfos.bulkAdd(pageInfosToStore);
            return numericId;
        });
        return { id: newDocId, type: 'pdf' };
    }
    async getDocument(identity) {
        await this.ensureInitialized();
        return await this.pdfDocuments.get(identity.id);
    }
    async getDocumentWithParagraphs(identity) {
        await this.ensureInitialized();
        const document = await this.pdfDocuments.get(identity.id);
        if (!document) {
            return undefined;
        }
        const paragraphs = await this.pdfParagraphs.where({ documentId: identity.id }).toArray();
        return { document, paragraphs };
    }
    /**
     * 按页获取PDF文档的段落数据
     * @param documentId PDF文档的数字ID
     * @param pageNumber 页码 (1-based)
     * @returns 该页的段落记录数组
     */
    async getParagraphsForPage(documentId, pageNumber) {
        await this.ensureInitialized();
        return await this.pdfParagraphs.where({ documentId, pageNumber }).toArray();
    }
    async getDocumentMetadata(identity) {
        await this.ensureInitialized();
        const document = await this.pdfDocuments.get(identity.id);
        if (document) {
            const { pdfContent: _pdfContent, ...metadata } = document;
            return metadata;
        }
        return undefined;
    }
    async updateDocument(identity, changes) {
        await this.ensureInitialized();
        await this.pdfDocuments.update(identity.id, changes);
        return identity;
    }
    async deleteDocument(identity) {
        await this.ensureInitialized();
        // 在事务中同时删除文档、页面信息和段落
        await dbManager.transaction('rw', ['pdfDocuments', 'pdfPageInfos', 'pdfParagraphs'], async () => {
            await this.pdfDocuments.delete(identity.id);
            await this.pdfPageInfos.where({ documentId: identity.id }).delete();
            await this.pdfParagraphs.where({ documentId: identity.id }).delete();
        });
    }
    /**
     * 获取PDF文档的所有页面缓存信息
     * @param documentId PDF文档的数字ID
     * @returns 页面信息数组
     */
    async getPageInfos(documentId) {
        await this.ensureInitialized();
        return this.pdfPageInfos.where({ documentId }).sortBy('pageIndex');
    }
    /**
     * 批量更新/插入PDF文档的页面缓存信息
     * @param pageInfos 要更新的页面信息数组
     */
    async bulkUpdatePageInfos(pageInfos) {
        await this.ensureInitialized();
        await this.pdfPageInfos.bulkPut(pageInfos);
    }
    /**
     * 获取PDF文档列表（支持分页和搜索）
     * @param options 查询选项 { limit, offset, searchText, sortBy }
     * @returns PdfDocument 数组
     */
    async getDocuments(options = {}) {
        await this.ensureInitialized();
        // 设置默认值
        const { limit = 20, offset = 0, searchText, sortBy = 'timestamp-desc' } = options;
        // 从 Collection 开始构建查询
        let collection = this.pdfDocuments.toCollection();
        // 首先应用过滤
        if (searchText) {
            const lowerSearchText = searchText.toLowerCase();
            collection = collection.filter(doc => doc.title?.toLowerCase().includes(lowerSearchText) ||
                doc.filename?.toLowerCase().includes(lowerSearchText) // 搜索 title 和 filename
            );
        }
        // 首先将所有匹配的文档获取到内存中
        const results = await collection.toArray();
        // 对内存中的数组应用排序
        if (sortBy === 'time-asc') {
            // 或者使用更明确的 'timestamp-asc'
            results.sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
        }
        else {
            // 默认为 'timestamp-desc'
            results.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
        }
        // 对排序后的内存中数组应用分页
        return results.slice(offset, offset + limit);
    }
    /**
     * 获取PDF文档数量
     * @returns PDF文档数量
     */
    async getDocumentCount() {
        await this.ensureInitialized();
        return await this.pdfDocuments.count();
    }
    async getAllDocumentsMetadata() {
        await this.ensureInitialized();
        const documentsArray = await this.pdfDocuments.orderBy('timestamp').reverse().toArray();
        return documentsArray.map((doc) => {
            const { pdfContent: _pdfContent, ...metadata } = doc;
            return metadata;
        });
    }
    async transaction(callback) {
        await this.ensureInitialized();
        return dbManager.transaction('rw', ['pdfDocuments', 'pdfPageInfos'], callback);
    }
}
PdfDocumentStorageProvider.instance = null;
//# sourceMappingURL=PdfDocumentStorageProvider.js.map